import { useEffect, useState } from 'react';
import { bookmarkService } from '../../../services/bookmark-service/bookmark-service';
import {
    BookmarkContent,
    BookmarkDataResponse,
} from '../../../models/bookmark-data';
import { UiFeature } from '../../../hooks/dyf/use-your-vehicle-features/vehicle-features.types';

interface UseFeatureArticleBookmarksProps {
    userGuid: string;
    vin: string | null;
}

export interface UseBookmarks {
    bookmarksData: BookmarkDataResponse;
    fetchBookmarks: () => Promise<BookmarkDataResponse>;
    onBookmarkIconClick: (item: UiFeature) => void;
    isBookmarkedFeature: (topicId: string) => boolean;
    loading: boolean;
}

export const initialBookmarkState: BookmarkDataResponse = {
    content: [],
    count: 0,
};

const useFeatureArticleBookmarks = ({
    userGuid,
    vin,
}: UseFeatureArticleBookmarksProps): UseBookmarks => {
    const [bookmarksData, setBookmarksData] = useState<BookmarkDataResponse>(
        initialBookmarkState
    );
    const [loading, setLoading] = useState<boolean>(false);

    const fetchBookmarks = async (): Promise<BookmarkDataResponse> => {
        if (!userGuid || !vin) return Promise.resolve(initialBookmarkState);

        setLoading(true);

        const response = await bookmarkService
            .getBookmarks(userGuid, vin)
            .then(data => {
                if (data && data.content) {
                    setBookmarksData(data);
                    return data;
                } else {
                    setBookmarksData(initialBookmarkState);
                    return initialBookmarkState;
                }
            });

        setLoading(false);
        return Promise.resolve(response);
    };

    const getBookmarkArticle = (topicId: string) => {
        const content = bookmarksData?.content;
        return (
            content?.length &&
            content.find(
                (bookmark: BookmarkContent) =>
                    bookmark.personalizationValueOne == topicId
            )
        );
    };

    const isBookmarkedFeature = (topicId: string) => {
        return !!getBookmarkArticle(topicId);
    };

    const onBookmarkIconClick = (item: UiFeature) => {
        if (vin && item.topicId) {
            if (isBookmarkedFeature(item.topicId.toString())) {
                const bookmark: any = getBookmarkArticle(
                    item.topicId.toString()
                );
                bookmarkService
                    .deleteBookmark(bookmark.bookmarkIndex)
                    .then(() => {
                        fetchBookmarks();
                    });
            } else {
                bookmarkService
                    .addBookmark(userGuid, vin, item.topicId.toString(), '1')
                    .then(() => {
                        fetchBookmarks();
                    });
            }
        }
    };

    useEffect(() => {
        fetchBookmarks();
    }, [userGuid, vin]);

    return {
        bookmarksData,
        fetchBookmarks,
        onBookmarkIconClick,
        isBookmarkedFeature,
        loading,
    };
};

export default useFeatureArticleBookmarks;
